import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface ClientDetail {
  clientId: string;
  displayName: string;
  source: string;
  primaryContactName: string | null;
  primaryBrokerDisplay: string | null;
  officeId: string | null;
  loanIds: string[];
  loansByEntity: Record<string, string[]>;
  earliestSettlementDate: string | null;
  hasActiveLoan: boolean;
  email: string | null;
  phone: string | null;
  dob: string | null;
  fixedRateExpiry: string | null;
  ioExpiry: string | null;
  preferredContactMethod: string | null;
  segment: string | null;
  notes: string | null;
  lastContactAt: unknown;
  cadenceOverride: unknown;
  status: string;
  ambiguousIdentity: boolean;
}

export interface ClientLoanDetail {
  loanId: string;
  lender: string | null;
  settlementDate: string | null; // 'YYYY-MM-DD'
  lastCommissionMonth: string | null; // 'YYYY-MM'
  isActive: boolean;
}

export interface ClientDetailEvent {
  eventId: string;
  eventType: string;
  status: string;
  occurrenceKey: string;
  dueAt: unknown;
  escalateAt: unknown;
  contactLoggedAt: unknown;
  /**
   * 'client_care' | 'trail_risk'. Null on legacy documents written before the
   * family existed; those predate trail-risk, so absent means cadence.
   *
   * Load-bearing in the drawer: trail-risk events are per loan AND per period and
   * can only be closed by exact eventId, so they are excluded from the
   * "re: event type" picker rather than offered as an action that silently fails.
   */
  eventFamily: string | null;
  /** Trail-risk only: the loan this occurrence belongs to. */
  trailRiskLoanId: string | null;
  /** Trail-risk only: 'YYYY-MM' of the occurrence. */
  trailRiskPeriodMonth: string | null;
}

export interface ClientContactEntry {
  id: string;
  contactedByPhone: boolean;
  contactedByEmail: boolean;
  phoneDate: string | null;
  emailDate: string | null;
  comments: string;
  contactBroker: string | null;
  createdBy: string | null;
  createdAt: unknown;
}

export interface ClientDetailCapabilities {
  canLogActivity: boolean;
  canEditProfile: boolean;
  canCreateFollowUp: boolean;
  canDismissEvent: boolean;
  scopeSource: 'owner' | 'linked_broker' | 'loan_book_grant' | 'office_admin' | 'company_admin' | null;
}

/**
 * Two shapes, and the callable picks one. A client that has been merged into
 * another returns ONLY the forwarding address — no profile, no events, no
 * contacts — so a stale link cannot render a frozen copy of someone's details.
 * Everything below is therefore optional on the wire.
 */
interface GetClientDetailResponse {
  client?: ClientDetail;
  events?: ClientDetailEvent[];
  contacts?: ClientContactEntry[];
  capabilities?: ClientDetailCapabilities;
  loans?: ClientLoanDetail[];
  /** True when this id is a merge tombstone; `mergedInto` is the live client. */
  merged?: boolean;
  mergedInto?: string | null;
}

export function useClientDetail(companyId?: string, clientId?: string | null) {
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [events, setEvents] = useState<ClientDetailEvent[]>([]);
  const [contacts, setContacts] = useState<ClientContactEntry[]>([]);
  const [capabilities, setCapabilities] = useState<ClientDetailCapabilities | null>(null);
  const [loans, setLoans] = useState<ClientLoanDetail[]>([]);
  // Merge redirect: set when the requested id is a tombstone. Kept separate from
  // `error` because it is not a failure — there IS a client, at another id.
  const [mergedInto, setMergedInto] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!companyId || !clientId) {
      setClient(null);
      setEvents([]);
      setContacts([]);
      setCapabilities(null);
      setLoans([]);
      setMergedInto(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<Record<string, unknown>, GetClientDetailResponse>(functions, 'getClientDetail');
      const res = await fn({ companyId, clientId });
      setClient(res.data.client ?? null);
      setEvents(res.data.events ?? []);
      setContacts(res.data.contacts ?? []);
      setCapabilities(res.data.capabilities ?? null);
      setLoans(res.data.loans ?? []);
      setMergedInto(res.data.merged ? (res.data.mergedInto ?? null) : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load client');
      console.error('[useClientDetail] failed:', e);
    } finally {
      setLoading(false);
    }
  }, [companyId, clientId]);

  useEffect(() => {
    // Fail closed across drawer navigation: never render the previous client's
    // capabilities while the next client's detail is still loading.
    setClient(null);
    setEvents([]);
    setContacts([]);
    setCapabilities(null);
    setLoans([]);
    setMergedInto(null);
    void refetch();
  }, [refetch]);

  return { client, events, contacts, capabilities, loans, mergedInto, loading, error, refetch };
}
